
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
} from "@/components/ui/command";
import { SearchResults } from "./SearchResults";
import { useSearch, type SearchResult } from "@/hooks/useSearch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export interface SearchCommandMenuProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
}

export function SearchCommandMenu({ open = false, onOpenChange, onClose }: SearchCommandMenuProps) {
  const [query, setQuery] = useState("");
  const [internalOpen, setInternalOpen] = useState(false);
  const { results, isLoading } = useSearch(query, open ?? internalOpen);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Handle controlled and uncontrolled states
  const isControlled = open !== undefined && onOpenChange !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setIsOpen = useCallback((value: boolean) => {
    if (isControlled) {
      onOpenChange(value);
      if (!value && onClose) {
        onClose();
      }
    } else {
      setInternalOpen(value);
    }
  }, [isControlled, onOpenChange, onClose]);

  // Reset query when dialog is opened
  useEffect(() => {
    if (isOpen) {
      setQuery("");
    }
  }, [isOpen]);

  const navigateToResult = useCallback((result: SearchResult) => {
    // Handle special cases based on result type
    if (result.type === "user") {
      // If the user clicked on their own profile
      if (user && user.id === result.id) {
        navigate("/profile");
      } else {
        navigate(result.url);
      }
      
      toast.success(`Viewing ${result.title}'s profile`);
    } else if (result.type === "group") {
      navigate(result.url);
      toast.success(`Viewing ${result.title} community`);
    } else if (result.type === "prompt") {
      navigate(result.url);
      toast.success(`Viewing prompt: ${result.title}`);
    } else {
      navigate(result.url);
    }
    
    setIsOpen(false);
  }, [navigate, setIsOpen, user]);

  return (
    <CommandDialog 
      open={isOpen} 
      onOpenChange={setIsOpen} 
      className="top-16 translate-y-0 rounded-t-none border-t-0 max-w-md mx-auto"
    >
      <Command className="rounded-lg border shadow-md">
        <CommandInput 
          placeholder="Search users, communities, prompts..." 
          value={query}
          onValueChange={setQuery}
          autoFocus
        />
        <CommandList>
          <SearchResults
            isLoading={isLoading}
            query={query}
            results={results}
            onSelect={navigateToResult}
          />
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
